import { Box } from "@mui/material";
import React from "react";
import useTheme from "../../../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../../../providers/ThemeProvider";
import { calculateNutrition } from "../../helpers/nutritionCalculations";
import MacroRingWithLegend from "../intake/MacroRingWithLegend";
import {
  NutritionDeclarationType,
  PlanMealItemCommonType,
} from "../../model/nutritionPlanTypes";

type Props = {
  mealItem: PlanMealItemCommonType;
};

const MICRONUTRIENTS: {
  key: keyof NutritionDeclarationType;
  label: string;
  unit: string;
}[] = [
  { key: "fibers_grams", label: "Fiber", unit: "g" },
  { key: "sugars_grams", label: "Sugars", unit: "g" },
  { key: "saturated_fat_grams", label: "Saturated fat", unit: "g" },
  { key: "cholesterol_milligrams", label: "Cholesterol", unit: "mg" },
  { key: "sodium_milligrams", label: "Sodium", unit: "mg" },
  { key: "potassium_milligrams", label: "Potassium", unit: "mg" },
  { key: "calcium_milligrams", label: "Calcium", unit: "mg" },
  { key: "iron_milligrams", label: "Iron", unit: "mg" },
  { key: "magnesium_milligrams", label: "Magnesium", unit: "mg" },
  { key: "vitamin_d_micrograms", label: "Vitamin D", unit: "µg" },
  { key: "vitamin_c_milligrams", label: "Vitamin C", unit: "mg" },
  { key: "omega3_grams", label: "Omega-3", unit: "g" },
  { key: "iodine_micrograms", label: "Iodine", unit: "µg" },
];

/** Same inputs the mobile `NutritionFactsSection` feeds `calculateNutrition`. */
const getDeclaration = (
  mealItem: PlanMealItemCommonType
): NutritionDeclarationType => {
  if (mealItem.food) {
    const food = mealItem.food;
    const servings = food.number_of_servings || 1;
    const unit = food.user_friendly_units?.find(
      (entry) => entry.id === food.selected_user_friendly_unit_id
    );
    return unit
      ? calculateNutrition(food, servings, unit)
      : calculateNutrition(food, (food.quantity || 100) / 100);
  }
  return calculateNutrition(mealItem.recipe!, 1);
};

/**
 * The app's `ItemNutritionFacts`: the segmented calorie ring with the macro
 * legend, then the micronutrients in two columns (a sheet behind a button on
 * mobile; the drawer has room to show them outright).
 */
const NutritionFactsSection = ({ mealItem }: Props) => {
  const theme: Theme = useTheme();

  const declaration = getDeclaration(mealItem);

  return (
    <Box sx={styles.section}>
      <Box sx={styles.title(theme)}>Nutrition facts</Box>
      <Box sx={styles.ring}>
        <MacroRingWithLegend
          cals={declaration.cals}
          carbs={declaration.carbs}
          protein={declaration.protein}
          fat={declaration.fat}
        />
      </Box>
      <Box sx={styles.microList}>
        {MICRONUTRIENTS.map(({ key, label, unit }) => (
          <Box key={key} sx={styles.microRow(theme)}>
            <Box>{label}</Box>
            <Box sx={styles.microValue(theme)}>
              {Math.round((declaration[key] ?? 0) * 10) / 10} {unit}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default NutritionFactsSection;

const styles = {
  section: {
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "flex-start",
    gap: "16px",
  },
  title: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  ring: {
    width: "100%",
  },
  // Two columns of label/value rows, filled top to bottom then left to right.
  microList: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    columnGap: "24px",
  },
  microRow: (theme: Theme) => ({
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 0",
    borderBottom: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  microValue: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
};
